import { useState, useEffect, useCallback } from 'react';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { BellRing, CircleAlert, CirclePlus, Check, X, History } from 'lucide-react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function ageLabel(iso) {
  try {
    const d = new Date(iso);
    const days = differenceInDays(new Date(), d);
    if (days > 7) return format(d, 'dd-MM-yyyy');
    if (days === 0) return 'Added today';
    return `Added ${days} day${days === 1 ? '' : 's'} ago`;
  } catch { return ''; }
}

export default function SupplierReminders({ supplier, onCountChange }) {
  const [reminders, setReminders] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchPending = useCallback(async () => {
    if (!supplier) return;
    try {
      const { data } = await api.get('/reminders', { params: { supplier, status: 'PENDING' } });
      setReminders(data);
      onCountChange?.(data.length);
    } catch (e) { console.error('Failed to fetch reminders:', e); }
  }, [supplier, onCountChange]);

  useEffect(() => { setReminders([]); fetchPending(); }, [fetchPending]);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.supplier === supplier) fetchPending(); };
    window.addEventListener('og-reminder-created', handler);
    return () => window.removeEventListener('og-reminder-created', handler);
  }, [supplier, fetchPending]);

  const complete = async (id, reason) => {
    try {
      await api.post(`/reminders/${id}/complete`, { reason });
      toast.success(reason === 'done' ? 'Marked as ordered/done' : "Noted — supplier didn't have it");
      fetchPending();
    } catch { toast.error('Could not update reminder'); }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get('/reminders', { params: { supplier, status: 'COMPLETED' } });
      setHistory(data);
    } catch (e) { console.error('Failed to fetch completed reminders:', e); }
  };

  if (!supplier || !reminders.length) return null;

  return (
    <div data-testid="supplier-reminders-panel" className="border border-amber-300 bg-amber-50/60 animate-fade-in">
      <div className="flex items-center justify-between px-4 py-2 border-b border-amber-200">
        <span className="flex items-center gap-2 text-xs font-bold tracking-[0.08em] uppercase text-amber-800">
          <BellRing className="h-3.5 w-3.5" /> Reminders for {supplier} ({reminders.length})
        </span>
        <button data-testid="reminder-history-link" onClick={openHistory}
          className="flex items-center gap-1 text-[11px] text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2">
          <History className="h-3 w-3" /> View History
        </button>
      </div>
      <div className="divide-y divide-amber-200/60">
        {reminders.map((r) => (
          <div key={r.id} data-testid={`reminder-card-${r.id}`} className="px-4 py-2.5 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                {r.reminder_type === 'OUT_OF_STOCK' ? (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 px-1.5 py-0 uppercase tracking-wide">
                    <CircleAlert className="h-2.5 w-2.5" /> Out of Stock
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-sky-700 bg-sky-100 border border-sky-200 px-1.5 py-0 uppercase tracking-wide">
                    <CirclePlus className="h-2.5 w-2.5" /> New Enquiry
                  </span>
                )}
                {r.priority === 'IMPORTANT' && (
                  <span className="text-[10px] font-bold text-amber-800 bg-amber-200 px-1.5 py-0 uppercase tracking-wide">Important</span>
                )}
                <span className="text-[10px] text-zinc-400">{ageLabel(r.created_at)}</span>
              </div>
              <p className="text-sm text-zinc-800 mt-1">{r.note}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button data-testid={`reminder-done-${r.id}`} onClick={() => complete(r.id, 'done')}
                className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors">
                <Check className="h-3 w-3" /> Ordered / Done
              </button>
              <button data-testid={`reminder-notavail-${r.id}`} onClick={() => complete(r.id, 'not_available')}
                className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 border border-zinc-300 text-zinc-500 hover:bg-zinc-100 transition-colors">
                <X className="h-3 w-3" /> Didn't have it
              </button>
            </div>
          </div>
        ))}
      </div>

      {historyOpen && (
        <Dialog open onOpenChange={setHistoryOpen}>
          <DialogContent data-testid="reminder-history-dialog" className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Reminder History — {supplier}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[360px] overflow-y-auto divide-y divide-zinc-100">
              {!history.length ? (
                <p className="text-sm text-zinc-400 py-6 text-center">No completed reminders yet</p>
              ) : history.map((r) => (
                <div key={r.id} className="py-2.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold px-1.5 py-0 uppercase tracking-wide ${
                      r.reminder_type === 'OUT_OF_STOCK' ? 'text-red-700 bg-red-50 border border-red-200' : 'text-sky-700 bg-sky-50 border border-sky-200'
                    }`}>{r.reminder_type === 'OUT_OF_STOCK' ? 'Out of Stock' : 'New Enquiry'}</span>
                    {r.priority === 'IMPORTANT' && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5">Important</span>}
                    <span className="text-[10px] text-zinc-400">
                      {r.completed_at ? format(new Date(r.completed_at), 'dd-MM-yyyy') : ''} · {r.completed_by} · {r.completed_reason}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-700 mt-0.5">{r.note}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
